import React, { useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';

import * as api from '../../../api';

import InputEmail from './InputEmail';
import InputPhone from './InputPhone';
import Otp from './Otp';

import { Button, Typography } from '@vivaahealth/design-system';

import styles from './index.module.css';

import { assertNever, parseValue } from 'common/dist/utils/types';
import { ContactMethod } from './types';

const { AuthErrorType } = api.Previsit;

interface Props {
  contactMethod: ContactMethod;
  email: string | null;
  phoneNumber: string | null;
  code?: string;
  backToContactPage: () => void;
  handleGenerateCode: (
    args:
      | { email: string; phoneNumber?: undefined }
      | { email?: undefined; phoneNumber: string },
  ) => Promise<void>;
  login: () => Promise<void>;
  setCode: (code?: string) => void;
}

const ContactsVerify: React.FC<Props> = (props) => {
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [input, setInput] = useState('');
  const [showOtp, setShowOtp] = useState(false);
  const { t } = useTranslation('login');
  const { description, params, submitText } = constructFields({
    ...props,
    input,
    t,
  });
  const onChangeInput = (v: string) => {
    setErrorMessage('');
    setInput(v);
  };
  const generateCode = async () => {
    setIsSubmitting(true);
    try {
      await props.handleGenerateCode(params);
      setShowOtp(true);
    } catch (err) {
      const loginError = parseValue<{ message: string }>(err.message);
      if (
        loginError?.message === AuthErrorType.EmailNotMatched ||
        loginError?.message === AuthErrorType.PhoneNotMatched
      ) {
        setErrorMessage('doesNotMatch');
      } else if (loginError?.message === AuthErrorType.MaxAttempts) {
        setErrorMessage('maxAttemptsReached');
      } else {
        setErrorMessage('errorLoggingIn');
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  const onEnterGenerateCode = async (key: string) => {
    if (key === 'Enter') await generateCode();
  };
  if (showOtp) return <Otp {...props} />;
  return (
    <>
      <div className={styles['description-container']}>
        <Typography variant="paragraph-large">{description}</Typography>
      </div>
      <div className={styles['text-field-container']}>
        {props.contactMethod === 'email' && (
          <InputEmail
            errorMessage={t(errorMessage)}
            onChangeInput={onChangeInput}
            onEnterGenerateCode={onEnterGenerateCode}
          />
        )}
        {props.contactMethod === 'sms' && (
          <InputPhone
            input={input}
            errorMessage={t(errorMessage)}
            onChangeInput={onChangeInput}
            onEnterGenerateCode={onEnterGenerateCode}
          />
        )}
        <Typography
          variant="title-medium"
          className={styles['text-field-subtitle-error']}
        >
          {t(errorMessage)}
        </Typography>
      </div>
      <Button
        className={styles['button-send-code']}
        disabled={!input || isSubmitting || !!errorMessage}
        fullWidth
        size="medium"
        onClick={async () => await generateCode()}
      >
        {isSubmitting ? submitText : t('sendCode')}
      </Button>
      <Button
        fullWidth
        size="medium"
        variant="outlined"
        onClick={() => props.backToContactPage()}
      >
        {t('cancel', { ns: 'common' })}
      </Button>
    </>
  );
};

export default ContactsVerify;

function constructFields(args: {
  contactMethod: ContactMethod;
  email: string | null;
  phoneNumber: string | null;
  input: string;
  t: TFunction;
}) {
  const { contactMethod, email, phoneNumber, input, t } = args;
  switch (contactMethod) {
    case 'email':
      return {
        description: t('addEmailAddressPrompt', { email }),
        params: { email: input },
        submitText: t('checking', { input: 'email' }),
      };
    case 'sms':
      return {
        description: t('addMobilePhoneNumberPrompt', { phoneNumber }),
        params: { phoneNumber: input },
        submitText: t('checking', { input: 'phone' }),
      };
    case 'none':
      throw new Error('not allowed method');
    default:
      assertNever('Unknown contactMethod', contactMethod);
  }
}
